import { LOCAL_LINE_COPY_KEYS, localLineCopy } from './localLineCopy.js';
import { GAME_DISPLAY_NAME, slotCasinoStringsEn } from './slotCasinoStrings.en.js';

const translationFallbackEn = slotCasinoStringsEn;

/** On-screen brand for the 40-line game. English in every locale, same rule as the 10-line title. */
export const LINES_40_DISPLAY_NAME = '40 Ancient Egypt Wilds';

/** On-screen brand for the 100-line game. English in every locale, same rule as the 10-line title. */
export const LINES_100_DISPLAY_NAME = '100 Ancient Egypt Wilds';

/**
 * Title and intro for the 10-line, 40-line, and 100-line games are owned by the client
 * (`localLineCopy.js`). Every other key still comes from the translation service.
 */
const WIDE_LINE_KEYS = new Set(['GAME_TITLE', 'INFO_INTRO_BODY']);
const WIDE_LINE_COPY = {
  '40': {
    prefix: '40_',
    displayName: LINES_40_DISPLAY_NAME,
    fallback: {
      INFO_INTRO_BODY:
        '{{gameTitle}} is a 5-reel, 40-line fixed video slot. The game has 11 symbols: 8 regular symbols, 1 Expanding Wild, and 2 Scatters. Line wins are paid left to right. Scatter wins are paid separately.',
    },
  },
  '100': {
    prefix: '100_',
    displayName: LINES_100_DISPLAY_NAME,
    fallback: {
      INFO_INTRO_BODY:
        '{{gameTitle}} is a 5-reel, 100-line fixed video slot. The game has 11 symbols: 8 regular symbols, 1 Expanding Wild, and 2 Scatters. Line wins are paid left to right. Scatter wins are paid separately.',
    },
  },
};

/** @type {'40' | '100' | null} */
let activeWideLines = null;
/** URL culture (`en`, `de-DE`, …). Language prefix selects the client-owned line copy. */
let activeCulture = 'en';

/** Call when the launch variant is known, before any player-facing copy is drawn. */
export function setTranslationVariant({ lines40 = false, lines100 = false } = {}) {
  activeWideLines = lines100 ? '100' : lines40 ? '40' : null;
}

/** Call when the launch culture is known, before any player-facing copy is drawn. */
export function setTranslationCulture(culture) {
  activeCulture = String(culture ?? 'en').trim() || 'en';
}

function activeWideCopy() {
  return activeWideLines ? WIDE_LINE_COPY[activeWideLines] : null;
}

function activeGameTitle() {
  return activeWideCopy()?.displayName ?? GAME_DISPLAY_NAME;
}

function applyReplace(text, replace) {
  if (!replace) return text;
  let out = text;
  for (const key of Object.keys(replace)) {
    out = out.split(`{{${key}}}`).join(replace[key] ?? '');
  }
  return out;
}

/** Spreadsheet/API copies often store line breaks as literal `\n` or `/n` instead of real newlines. */
function unescapeTranslationLineBreaks(text) {
  if (typeof text !== 'string') return text;
  if (!text.includes('\\') && !text.includes('/n')) return text;
  return text
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\/n/g, '\n');
}

function rewriteIntroTitle(map, titleKey, introKey, canonicalTitle) {
  const localizedTitle = typeof map[titleKey] === 'string' ? map[titleKey].trim() : '';
  if (!localizedTitle || localizedTitle === canonicalTitle) return;
  const intro = map[introKey];
  if (typeof intro === 'string' && intro.includes(localizedTitle)) {
    map[introKey] = intro.split(localizedTitle).join('{{gameTitle}}');
  }
}

function normalizeApiTranslations(fromApi) {
  const map = { ...(fromApi ?? {}) };
  rewriteIntroTitle(map, 'GAME_TITLE', 'INFO_INTRO_BODY', GAME_DISPLAY_NAME);
  for (const variant of Object.values(WIDE_LINE_COPY)) {
    rewriteIntroTitle(
      map,
      `${variant.prefix}GAME_TITLE`,
      `${variant.prefix}INFO_INTRO_BODY`,
      variant.displayName,
    );
  }
  delete map.GAME_TITLE;
  for (const key of LOCAL_LINE_COPY_KEYS) delete map[key];
  return map;
}

export class TranslationStore {
  constructor() {
    this.translations = {};
    this.translationsFetchOk = false;
  }

  setTranslations(fromApi) {
    this.translations = normalizeApiTranslations(fromApi);
  }

  setTranslationsFetchOk(ok) {
    this.translationsFetchOk = !!ok;
  }

  resolvedGameTitle() {
    const variant = activeWideCopy();
    if (!variant) return activeGameTitle();
    const localTitle = localLineCopy(`${variant.prefix}GAME_TITLE`, activeCulture);
    return localTitle ?? activeGameTitle();
  }

  t(key, replace) {
    const replaceWithGameTitle = { ...replace, gameTitle: this.resolvedGameTitle() };

    if (key === 'GAME_TITLE') {
      return unescapeTranslationLineBreaks(applyReplace(this.resolvedGameTitle(), replaceWithGameTitle));
    }

    const variant = activeWideCopy();
    const storageKey = variant && WIDE_LINE_KEYS.has(key) ? `${variant.prefix}${key}` : key;
    const owned = localLineCopy(storageKey, activeCulture);
    if (owned) {
      return unescapeTranslationLineBreaks(applyReplace(owned, replaceWithGameTitle));
    }
    const fromApi = this.translations[storageKey];
    const wideFallback = variant ? variant.fallback[key] : undefined;
    const fromFallback = wideFallback ?? translationFallbackEn[key];
    const base =
      typeof fromApi === 'string' && fromApi.length > 0
        ? fromApi
        : typeof fromFallback === 'string' && fromFallback.length > 0
          ? fromFallback
          : key;
    return unescapeTranslationLineBreaks(applyReplace(base, replaceWithGameTitle));
  }
}

export const translationStore = new TranslationStore();
